import "./Table.css";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { calcCellHeight } from "components/utils.js";
import { copyToClipboard } from "core/clipboard_utils";
import {
	changeNestedArrKeyValueOnCondition,
	copyArr,
	copyObj,
	getNextInArr,
	getPrevInArr,
	searchNestedIterableItem,
} from "core/iterable_utils";
import { getStrOrEmptyStrIfNull } from "core/str_utils";
import {
	clearClickedTableCtrlRows,
	pushMultiClickedTableRows,
	pushToClickedTableCtrlRows,
	removeFromClickedTableCtrlRows,
	setClickedTableClmName,
	setClickedTableRow,
	setMultiClickedTableRows,
} from "slices/ribbon";

import { getTableDefaultOptions } from "./TableDefaultOptions";
import TableMain from "./TableMain";

const Table = ({ rowData, columnsData, scrollBlock, options, funcs, tableRef }) => {
	const dispatch = useDispatch();

	const [sortParams, setSortParams] = useState({});
	const [filterParams, setFilterParams] = useState({});
	const [filterValues, setFilterValues] = useState({});

	const clickedTableRow = useSelector((state) => state.ribbon.clickedTableRow);
	const clickedTableClmName = useSelector((state) => state.ribbon.clickedTableClmName);
	const clickedTableCtrlRows = useSelector((state) => state.ribbon.clickedTableCtrlRows);
	const muiltiClickedTableRows = useSelector((state) => state.ribbon.muiltiClickedTableRows);

	const tableDefaultOptions = getTableDefaultOptions();
	const displayOptions = { ...tableDefaultOptions.displayOptions, ...(options.displayOptions != null ? options.displayOptions : {}) };
	const tableOptions = { ...getTableDefaultOptions(), ...options };

	const selectRow = (tableData, clickedRow, targetDom, parentRowObj, event) => {
		if (funcs?.selectDataCustom) {
			funcs.selectDataCustom(tableData, clickedRow, targetDom, parentRowObj, event);
			return;
		}
		if (tableOptions?.selectOptions?.controlled !== true) return;
		const clickedClmName = targetDom?.attributes?.clm?.value;

		if (tableOptions?.selectOptions?.preventSelectColumns) {
			if (tableOptions.selectOptions.preventSelectColumns.includes(clickedClmName)) {
				return;
			}
		}
		const nextSelectedRow = copyObj(clickedRow);
		if (!tableOptions?.selectOptions?.preventSetClickedTableRow) {
			dispatch(setClickedTableRow({ ...clickedRow, parentRow: parentRowObj }));
		}

		dispatch(setClickedTableClmName(clickedClmName));

		if (tableOptions.selectOptions.expandOnSelect?.preventExpandColumns) {
			if (tableOptions.selectOptions.expandOnSelect.preventExpandColumns.includes(clickedClmName)) {
				return;
			}
		}

		let newTableData = copyArr(rowData);

		if (tableOptions?.selectOptions?.multiple !== true) {
			newTableData = changeNestedArrKeyValueOnCondition({
				arr: newTableData,
				keySearch: "selected",
				valSearch: true,
				keyChange: "selected",
				valChange: false,
			});
		}

		if (tableOptions?.selectOptions?.aggrFooterCellSum && !event?.ctrlKey) {
			for (let ctrlRow of clickedTableCtrlRows) {
				newTableData = changeNestedArrKeyValueOnCondition({
					arr: newTableData,
					keySearch: "key",
					valSearch: ctrlRow.key,
					keyChange: "row_type_style",
					valChange: null,
				});
			}
		}

		if (nextSelectedRow) {
			newTableData = changeNestedArrKeyValueOnCondition({
				arr: newTableData,
				keySearch: "key",
				valSearch: nextSelectedRow.key,
				keyChange: "selected",
				valChange: true,
				callback: (row) => {
					if (tableOptions?.selectOptions?.expandOnSelect) {
						row.rowHeightMultiply = getRowMultiply({ rowObj: row });
					}
					if (tableOptions?.selectOptions?.aggrFooterCellSum) {
						if (tableOptions?.selectOptions?.aggrFooterCellSum?.columnsName?.includes(clickedClmName)) {
							aggrFooterCellSum({ rowObj: row, clickedClmName, event });
						}
					}
					return row;
				},
			});
		}

		for (let mltRow of muiltiClickedTableRows) {
			newTableData = changeNestedArrKeyValueOnCondition({
				arr: newTableData,
				keySearch: "key",
				valSearch: mltRow.key,
				keyChange: "multiSelected",
				valChange: false,
			});
		}
		dispatch(setMultiClickedTableRows([]));

		return funcs.setTableData({ newTableData: newTableData });
	};

	const multiSelectRow = ({ tableData, clickedRow, event }) => {
		let newTableData = copyArr(rowData);
		const curMultiRows = [];
		let startAdding = false;
		let finishAdding = false;

		if (clickedTableRow) curMultiRows.push(clickedTableRow);

		const pushToCurMultiRowsRecource = (rowData) => {
			for (let iterRow of rowData) {
				if (startAdding && !finishAdding) curMultiRows.push(iterRow);
				if (iterRow?.key === clickedTableRow?.key) startAdding = true;
				if (iterRow?.key === clickedRow?.key) {
					finishAdding = true;
					break;
				}
				if (iterRow?._children) {
					pushToCurMultiRowsRecource(iterRow?._children);
				}
			}
		};

		if (tableOptions?.selectOptions?.multipleControlledSelect?.ctrl && event?.ctrlKey) {
			curMultiRows.push(clickedRow);
		} else if (tableOptions?.selectOptions?.multipleControlledSelect?.shift && event?.shiftKey) {
			pushToCurMultiRowsRecource(rowData, false);
			if (curMultiRows?.length === 1) {
				startAdding = false;
				finishAdding = false;
				const reversedRowData = copyArr(rowData).reverse();
				pushToCurMultiRowsRecource(reversedRowData);
			}
		}

		for (let mltRow of curMultiRows) {
			dispatch(pushMultiClickedTableRows(mltRow));
			newTableData = changeNestedArrKeyValueOnCondition({
				arr: newTableData,
				keySearch: "key",
				valSearch: mltRow.key,
				callback: (row) => {
					row.selected = false;
					row.multiSelected = row.multiSelected === true ? false : true;
					return row;
				},
			});
		}
		dispatch(setClickedTableRow(null));

		return funcs.setTableData({ newTableData: newTableData });
	};

	const aggrFooterCellSum = ({ rowObj, clickedClmName, event }) => {
		if (event?.ctrlKey) {
			if (clickedTableCtrlRows.filter((x) => x.key === rowObj.key && x.pressedClm === clickedClmName).length > 0) {
				if (rowObj.row_type_style) {
					rowObj.row_type_style = String(rowObj.row_type_style).replace(`__${clickedClmName}__`, "");
					if (rowObj.row_type_style === "ctrl_multi_select") rowObj.row_type_style = null;
				}
				dispatch(removeFromClickedTableCtrlRows({ key: rowObj.key, pressedClm: clickedClmName }));
			} else {
				dispatch(pushToClickedTableCtrlRows({ ...rowObj, pressedClm: clickedClmName }));
				if (rowObj.row_type_style) {
					if (!String(rowObj.row_type_style).includes(`__${clickedClmName}__`)) {
						rowObj.row_type_style = `${rowObj.row_type_style}__${clickedClmName}__`;
					}
				} else {
					rowObj.row_type_style = `ctrl_multi_select__${clickedClmName}__`;
				}
			}
		} else {
			dispatch(clearClickedTableCtrlRows());
		}
	};

	const getRowMultiply = ({ rowObj }) => {
		const safeSpace = 100;
		const fontSize = tableOptions.selectOptions.expandOnSelect.fontSize;
		const tableClass = tableOptions.selectOptions.expandOnSelect.tableClass;
		const calcRowHeightMultiply = tableOptions.selectOptions.expandOnSelect.calcRowHeightMultiply;
		const rowHeightMultiplyArr = [];

		for (const clmName of tableOptions.selectOptions.expandOnSelect.columnsName) {
			let cellHeight;

			if (calcRowHeightMultiply && rowObj.model_name !== "Ksr") {
				cellHeight = calcRowHeightMultiply({
					rowObj,
					safeSpace,
					clmObj: columnsData.find((clm) => clm.name === clmName),
				});
				rowHeightMultiplyArr.push(cellHeight);
				continue;
			}

			cellHeight = calcCellHeight({
				fontSize,
				safeSpace,
				tableClass,
				clmName,
				cellText: rowObj[clmName]?.toString(),
			});
			rowHeightMultiplyArr.push(cellHeight);
		}

		const rowHeightMultiply = Math.max(...rowHeightMultiplyArr);

		if (rowObj.rowHeightMultiply == null || rowObj.rowHeightMultiply === 1) {
			return rowHeightMultiply;
		} else if (rowObj.rowHeightMultiply > 1) {
			return null;
		} else {
			return null;
		}
	};

	const setRowCheckBox = ({ rowObj, clmName, value }) => {
		const newTableData = changeNestedArrKeyValueOnCondition({
			arr: copyArr(rowData),
			keySearch: "key",
			valSearch: rowObj.key,
			keyChange: [clmName],
			valChange: value,
		});
		return funcs.setTableData({ newTableData: newTableData });
	};

	const changeRowChildrenVisible = ({ clickedRow, tableData }) => {
		let newTableData = copyObj(rowData);
		let newValue = true;
		if (clickedRow.childs_visible === true) newValue = false;
		if (clickedRow) {
			newTableData = changeNestedArrKeyValueOnCondition({
				arr: newTableData,
				keySearch: "key",
				valSearch: clickedRow.key,
				keyChange: "childs_visible",
				valChange: newValue,
				callback: (row) => {
					if (row._children) {
						row._children.forEach((ch) => {
							ch.visible = newValue;
							return ch;
						});
					}
					if (funcs?._post_changeRowChildrenVisible) {
						funcs?._post_changeRowChildrenVisible({ rowObj: row });
					}
					return row;
				},
			});
		}

		return funcs.setTableData({ newTableData: newTableData });
	};

	const changeErrorsDetailsVisible = (row) => {
		let clickedRow = row.row;
		if (clickedRow == null) clickedRow = row.clickedRow;

		let newTableData = copyObj(rowData);
		let newValue = true;
		if (clickedRow.errors_visible === true) newValue = false;
		if (clickedRow) {
			newTableData = changeNestedArrKeyValueOnCondition({
				arr: newTableData,
				keySearch: "key",
				valSearch: clickedRow.key,
				keyChange: "errors_visible",
				valChange: newValue,
				callback: (row) => {
					if (row._children) {
						row._details.forEach((ch) => {
							ch.visible = newValue;
							return ch;
						});
					}
					return row;
				},
			});
		}

		return funcs.setTableData({ newTableData: newTableData });
	};

	const changePaperDetailsVisible = ({ clickedRow, tableData }) => {
		if (tableOptions?.paperSettings?.use !== true) return;
		if (tableOptions?.paperSettings?.customOpenFunc) return;
		let newTableData = copyObj(tableData);

		newTableData = changeNestedArrKeyValueOnCondition({
			arr: newTableData,
			keySearch: "key",
			valSearch: clickedRow.key,
			keyChange: "paper_visible",
			valChange: true,
			callback: (row) => {
				if (row.paper_visible == null) {
					row.paper_visible = true;
				} else {
					row.paper_visible = !row.paper_visible;
				}
				return row;
			},
		});

		return funcs.setTableData({ newTableData: newTableData });
	};

	const getInitSortParams = useCallback(() => {
		const sort = { currentSortClms: [] };
		columnsData.forEach((clm) => {
			if (clm.sort) {
				sort[clm.name] = "down";
			}
		});
		return sort;
	}, [columnsData]);

	const sortData = ({ clmName, direction }) => {
		let customFunc;
		let standardSortCallback;
		let newDirection;

		const setParams = () => {
			setSortParams((prev) => ({
				...(tableOptions?.sortOptions?.multiSort ? prev : getInitSortParams()),
				[clmName]: newDirection,
				currentSortClms: [...(tableOptions?.sortOptions?.multiSort ? prev.currentSortClms : []), clmName],
			}));
		};

		if (direction === "up") {
			customFunc = funcs.customSortDataUp;
			standardSortCallback = (a, b) => (getStrOrEmptyStrIfNull(a[clmName]) < getStrOrEmptyStrIfNull(b[clmName]) ? 1 : -1);
			newDirection = "down";
		} else if (direction === "down") {
			customFunc = funcs.customSortDataDown;
			standardSortCallback = (a, b) => (getStrOrEmptyStrIfNull(a[clmName]) > getStrOrEmptyStrIfNull(b[clmName]) ? 1 : -1);
			newDirection = "up";
		}

		if (tableOptions?.sortOptions?.serverSort === true) {
			if (customFunc[clmName]) {
				customFunc[clmName]();
				setParams();
			}
		} else {
			let newTableData = copyObj(rowData);
			if (customFunc && customFunc[clmName]) {
				newTableData = customFunc[clmName]({ newTableData, clmName });
			} else {
				newTableData = newTableData.sort((a, b) => standardSortCallback(a, b));
			}
			setParams();
			funcs.setTableData({ newTableData: newTableData });
		}
	};

	const filterData = (rowsData, clmName, strValue) => {
		if (tableOptions?.filterOptions?.serverFilter === true) {
			if (funcs.customFilter) {
				funcs.customFilter([{ name: clmName, value: strValue }]);
			}
		} else if (tableOptions?.filterOptions?.customFilter) {
			tableOptions.filterOptions.customFilter([{ name: clmName, value: strValue }]);
		} else {
			const newTableData = copyObj(rowData).map((row) => {
				row.visible = true;
				if (!String(row[clmName]).toLowerCase().includes(String(strValue).toLowerCase())) {
					row.visible = false;
				}
				return row;
			});
			funcs.setTableData({ newTableData: newTableData });
		}
	};

	const updateFilterStatus = (clmName, status) => {
		setFilterParams((prev) => ({ ...prev, [clmName]: status }));
	};

	const columnsDataNames = useMemo(() => {
		const columnsFlatNames = [];
		for (let clm of columnsData) {
			if (clm.children == null) {
				columnsFlatNames.push(clm.name);
			} else {
				for (let ch of clm.children) columnsFlatNames.push(ch.name);
			}
		}
		return columnsFlatNames;
	}, [columnsData]);

	const selectPrevNextRow = ({ direction }) => {
		const { prevItem, nextItem } = searchNestedIterableItem({
			iterable: rowData,
			isSeekRowCallback: (row) => row.key && row.key === clickedTableRow?.key,
			isPermittedRowCallback: (row) => row.visible !== false,
			isPermittedChildsLoopCallback: (row) => row.childs_visible !== false,
		});
		let seekItem;
		if (direction === "prev") {
			seekItem = prevItem;
		} else if (direction === "next") {
			seekItem = nextItem;
		}
		if (!seekItem) seekItem = { ...clickedTableRow };
		return seekItem;
	};

	const navigateByKeyboard = ({ keyName }) => {
		if (keyName === "ArrowLeft") {
			dispatch(setClickedTableClmName(getPrevInArr({ arr: columnsDataNames, value: clickedTableClmName, reverse: true })));
		} else if (keyName === "ArrowRight") {
			dispatch(setClickedTableClmName(getNextInArr({ arr: columnsDataNames, value: clickedTableClmName, reverse: true })));
		} else if (keyName === "ArrowUp") {
			dispatch(setClickedTableRow(selectPrevNextRow({ direction: "prev" })));
		} else if (keyName === "ArrowDown") {
			dispatch(setClickedTableRow(selectPrevNextRow({ direction: "next" })));
		}
	};

	const keyboardActionOnKeyDown = ({ event }) => {
		if (tableOptions?.keyboardOptions?.navigation && ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(event.key)) {
			event.preventDefault();
			navigateByKeyboard({ keyName: event.key });
		} else if (tableOptions?.keyboardOptions?.copy && event?.ctrlKey === true) {
			if (["c", "с"].includes(event.key)) {
				event.preventDefault();
				copyToClipboard(clickedTableRow?.[clickedTableClmName]);
				dispatch(setClickedTableRow({ ...clickedTableRow }));
			} else if (event.key === "Control") {
				event.preventDefault();
				dispatch(setClickedTableRow({ ...clickedTableRow }));
			}
		} else {
			funcs.keyboardAction.onKeyDown({ event });
		}
	};

	useEffect(() => {
		setSortParams((prev) => ({ ...getInitSortParams(), ...prev }));
	}, [getInitSortParams]);

	useEffect(() => {
		const flt = {};
		const fltVals = {};
		for (let clm of columnsData) {
			if (clm.filter) flt[clm.name] = "close";
		}

		if (tableOptions?.filterOptions?.prevFiltredData) {
			Object.keys(tableOptions?.filterOptions?.prevFiltredData).forEach((key) => {
				const value = tableOptions?.filterOptions?.prevFiltredData[key];
				if (value != null && String(value).length > 0) {
					flt[key] = "open";
					fltVals[key] = value;
				}
			});
		}
		setFilterParams((prev) => flt);
		setFilterValues((prev) => fltVals);
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		const keydownAction = (event) => keyboardActionOnKeyDown({ event });
		const clickShiftAction = (event) => {
			if ((clickedTableRow?.inline_cell != null) & (clickedTableRow?.inline_cell !== "")) return;
			if (event?.shiftKey && clickedTableRow) window.getSelection().removeAllRanges();
		};

		if (funcs.keyboardAction && clickedTableClmName && clickedTableRow) {
			document.addEventListener("keydown", keydownAction, {
				once: true,
			});
		}
		if (tableOptions?.selectOptions?.multipleControlledSelect?.shift) {
			document.addEventListener("keydown", clickShiftAction, {
				once: true,
			});
		}
		return () => {
			document.removeEventListener("keydown", keydownAction);
			document.removeEventListener("keydown", clickShiftAction);
		};
	}, [clickedTableClmName, clickedTableRow]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="KpsrTable" cell_border={String(displayOptions?.addBorderForClickedCell)}>
			<TableMain
				rows={{ data: rowData }}
				columns={columnsData}
				options={{
					selectable: tableOptions?.selectOptions?.controlled,
					selectableUncontrolled: tableOptions?.selectOptions?.uncontrolled,
					scrollBlock: scrollBlock,
					scrollHeightControll: displayOptions.scrollHeightControll,
					headScrollControllBlock: displayOptions.headScrollControllBlock,
					clickedTableClmName: clickedTableClmName,
					rowsDisplayCount: displayOptions.rowsDisplayCount,
					rowHeight: displayOptions.rowHeight,
					targerRowHeight: displayOptions.targetRowHeight,
					displayScrollDelay: displayOptions.displayScrollDelay,
					allowInitScroll: displayOptions.allowInitScroll,
					addH: displayOptions.additinalScrollHeight,
					columnsWithMinWidth: displayOptions.columnsWithMinWidth,
					newResizableColumns: displayOptions.newResizableColumns,
					localStoragePageKey: displayOptions.localStoragePageKey,
					headBlocks: displayOptions.headBlocks,
					advSeachActiveRow: displayOptions.advSeachActiveRow,
					detailsClms: displayOptions.detailsClms,
					funcCall: tableOptions?.funcCall ?? "local",
					singleClickOptions: {
						useTargetParentNode: tableOptions.singleClickOptions.useTargetParentNode,
						useStringInclude: tableOptions.singleClickOptions.useTargetParentNode,
					},
					useEditIcon: displayOptions.useEditIcon,
					resizableColumns: tableOptions.resizableColumns,
					columnsSettingsName: tableOptions.columnsSettingsName,
					userSettings: tableOptions.userSettings,
					selecting: tableOptions.selectingInputOptions,
					treeChildPaddingLeft: tableOptions?.nestingOptions?.childPaddingLeft,
					treeControlData: tableOptions?.nestingOptions?.treeControlData,
					paperSettings: tableOptions?.paperSettings,
					customRowDetail: tableOptions?.customRowDetail,
					tableType: tableOptions?.tableType,
					navigate: tableOptions?.navigate,
					originColumnsData: tableOptions?.originColumnsData,
					preventOnFocusEditing: tableOptions?.editingOptions?.preventOnFocusEditing,
					...(tableOptions?.tableSpecificOptions || {}),
					columnsData: columnsData,
					multipleControlledSelect: tableOptions?.selectOptions?.multipleControlledSelect,
					calculatedRowStyle: displayOptions?.calculatedRowStyle,
				}}
				func={{
					selectDataLocal: selectRow,
					multiSelectRow: multiSelectRow,
					setTableData: funcs.setTableData,
					changeVisible: changeRowChildrenVisible,
					changePaperDetailsVisible: changePaperDetailsVisible,
					changeErrorsDetailsVisible: changeErrorsDetailsVisible,
					setCheckedFunc: setRowCheckBox,
					changeVisibleOptions: tableOptions?.nestingOptions?.changeChildrenVisible,
					editDataFactory: funcs.editDataFactory,
					updateFilterValuesLocal: funcs.updateFilterValuesLocal,
					doubleClickRowAction: funcs.doubleClickRowAction,
					sortDataUp: (rowsData, clmName) => sortData({ clmName: clmName, direction: "up" }),
					sortDataDown: (rowsData, clmName) => sortData({ clmName: clmName, direction: "down" }),
					filterData: filterData,
					updateFilterStatus: updateFilterStatus,
					singleClickRowAction: {
						tagName: tableOptions?.singleClickOptions?.tagName,
						action: tableOptions?.singleClickOptions?.action,
					},
					search: {
						advancedSearchParams: funcs?.search?.advancedSearchParams,
						setAdvancedSearchParams: funcs?.search?.setAdvancedSearchParams,
					},
					...funcs.customFuncs,
				}}
				sortParams={sortParams}
				filterParams={filterParams}
				filterValues={filterValues}
			></TableMain>
		</div>
	);
};

export default Table;
